import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const inputStyle = {
  width: "100%",
  padding: "0.75rem 1rem",
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  fontSize: "0.95rem",
  background: "white",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  marginBottom: "0.4rem",
  fontWeight: "600",
  color: "#374151",
  fontSize: "0.88rem",
};

const sectionStyle = {
  background: "white",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  padding: "1.75rem",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  marginBottom: "1.5rem",
};

export default function Profile({ currentUser, setCurrentUser }) {
  const navigate = useNavigate();
  const [info, setInfo] = useState({
    first_name: currentUser?.first_name || "",
    last_name: currentUser?.last_name || "",
    email: currentUser?.email || "",
    host_institution: currentUser?.host_institution || "",
  });
  const [infoMsg, setInfoMsg] = useState({ text: "", ok: true });
  const [infoSaving, setInfoSaving] = useState(false);

  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState({ text: "", ok: true });
  const [pwSaving, setPwSaving] = useState(false);

  if (!currentUser) {
    navigate("/login");
    return null;
  }

  const displayName = currentUser.first_name || currentUser.username;
  const initials = displayName.charAt(0).toUpperCase();

  const handleInfoSave = async (e) => {
    e.preventDefault();
    setInfoSaving(true);
    setInfoMsg({ text: "", ok: true });
    try {
      const r = await fetch("/api/me", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(info),
      });
      if (r.ok) {
        setCurrentUser(u => ({ ...u, ...info }));
        setInfoMsg({ text: "Profile updated successfully.", ok: true });
      } else {
        setInfoMsg({ text: await r.text(), ok: false });
      }
    } catch {
      setInfoMsg({ text: "Network error. Please try again.", ok: false });
    } finally {
      setInfoSaving(false);
    }
  };

  const handlePwSave = async (e) => {
    e.preventDefault();
    setPwMsg({ text: "", ok: true });
    if (pw.newPassword !== pw.confirm) {
      setPwMsg({ text: "New passwords do not match.", ok: false });
      return;
    }
    if (pw.newPassword.length < 8) {
      setPwMsg({ text: "New password must be at least 8 characters.", ok: false });
      return;
    }
    setPwSaving(true);
    try {
      const r = await fetch("/api/me/password", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currentPassword: pw.currentPassword, newPassword: pw.newPassword }),
      });
      if (r.ok) {
        setPw({ currentPassword: "", newPassword: "", confirm: "" });
        setPwMsg({ text: "Password changed successfully.", ok: true });
      } else {
        setPwMsg({ text: await r.text(), ok: false });
      }
    } catch {
      setPwMsg({ text: "Network error. Please try again.", ok: false });
    } finally {
      setPwSaving(false);
    }
  };

  const msgBox = (msg) => msg.text ? (
    <div style={{
      marginTop: "0.75rem",
      padding: "0.65rem 1rem",
      borderRadius: "7px",
      fontSize: "0.88rem",
      fontWeight: 500,
      background: msg.ok ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
      color: msg.ok ? "#047857" : "#dc2626",
      border: `1px solid ${msg.ok ? "#6ee7b7" : "#fca5a5"}`,
    }}>{msg.text}</div>
  ) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "2.5rem 1.5rem",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ maxWidth: "680px", margin: "0 auto" }}>

        {/* Avatar + name header */}
        <div style={{ display: "flex", alignItems: "center", gap: "1.25rem", marginBottom: "2rem" }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "#0079C0",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1.6rem", fontWeight: "700", color: "#fff", flexShrink: 0,
          }}>
            {initials}
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: "700", color: "#0f172a" }}>
              {displayName}
            </h1>
            <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>@{currentUser.username}</span>
            {currentUser.is_admin && (
              <span style={{
                marginLeft: "0.5rem",
                background: "rgba(0,121,192,0.1)", color: "#0079C0",
                borderRadius: "12px", padding: "1px 8px",
                fontSize: "0.75rem", fontWeight: "700",
              }}>Admin</span>
            )}
          </div>
        </div>

        {/* Profile Info */}
        <div style={sectionStyle}>
          <h2 style={{ margin: "0 0 1.25rem", fontSize: "1.1rem", fontWeight: "700", color: "#0f172a" }}>
            Profile Information
          </h2>
          <form onSubmit={handleInfoSave} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div>
                <label style={labelStyle}>First Name</label>
                <input
                  style={inputStyle}
                  value={info.first_name}
                  onChange={e => setInfo(i => ({ ...i, first_name: e.target.value }))}
                  placeholder="First name"
                  onFocus={e => { e.target.style.borderColor = "#0079C0"; e.target.style.boxShadow = "0 0 0 3px rgba(0,121,192,0.1)"; }}
                  onBlur={e => { e.target.style.borderColor = "#d1d5db"; e.target.style.boxShadow = "none"; }}
                />
              </div>
              <div>
                <label style={labelStyle}>Last Name</label>
                <input
                  style={inputStyle}
                  value={info.last_name}
                  onChange={e => setInfo(i => ({ ...i, last_name: e.target.value }))}
                  placeholder="Last name"
                  onFocus={e => { e.target.style.borderColor = "#0079C0"; e.target.style.boxShadow = "0 0 0 3px rgba(0,121,192,0.1)"; }}
                  onBlur={e => { e.target.style.borderColor = "#d1d5db"; e.target.style.boxShadow = "none"; }}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                style={inputStyle}
                type="email"
                value={info.email}
                onChange={e => setInfo(i => ({ ...i, email: e.target.value }))}
                placeholder="email@example.com"
                onFocus={e => { e.target.style.borderColor = "#0079C0"; e.target.style.boxShadow = "0 0 0 3px rgba(0,121,192,0.1)"; }}
                onBlur={e => { e.target.style.borderColor = "#d1d5db"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <div>
              <label style={labelStyle}>Host Institution</label>
              <input
                style={inputStyle}
                value={info.host_institution}
                onChange={e => setInfo(i => ({ ...i, host_institution: e.target.value }))}
                placeholder="University / Research Institute"
                onFocus={e => { e.target.style.borderColor = "#0079C0"; e.target.style.boxShadow = "0 0 0 3px rgba(0,121,192,0.1)"; }}
                onBlur={e => { e.target.style.borderColor = "#d1d5db"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <div>
              <label style={labelStyle}>Username</label>
              <input
                style={{ ...inputStyle, background: "#f1f5f9", color: "#6b7280", cursor: "not-allowed" }}
                value={currentUser.username}
                disabled
              />
            </div>
            <button
              type="submit"
              disabled={infoSaving}
              style={{
                alignSelf: "flex-start",
                background: "#0079C0",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "0.6rem 1.5rem",
                fontWeight: "600",
                fontSize: "0.95rem",
                cursor: infoSaving ? "not-allowed" : "pointer",
                opacity: infoSaving ? 0.7 : 1,
                transition: "background 0.15s",
              }}
            >
              {infoSaving ? "Saving…" : "Save Changes"}
            </button>
            {msgBox(infoMsg)}
          </form>
        </div>

        {/* Change Password */}
        <div style={sectionStyle}>
          <h2 style={{ margin: "0 0 1.25rem", fontSize: "1.1rem", fontWeight: "700", color: "#0f172a" }}>
            Change Password
          </h2>
          <form onSubmit={handlePwSave} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <label style={labelStyle}>Current Password</label>
              <input
                style={inputStyle}
                type="password"
                value={pw.currentPassword}
                onChange={e => setPw(p => ({ ...p, currentPassword: e.target.value }))}
                placeholder="Current password"
                onFocus={e => { e.target.style.borderColor = "#0079C0"; e.target.style.boxShadow = "0 0 0 3px rgba(0,121,192,0.1)"; }}
                onBlur={e => { e.target.style.borderColor = "#d1d5db"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <div>
              <label style={labelStyle}>New Password</label>
              <input
                style={inputStyle}
                type="password"
                value={pw.newPassword}
                onChange={e => setPw(p => ({ ...p, newPassword: e.target.value }))}
                placeholder="New password (min. 8 characters)"
                onFocus={e => { e.target.style.borderColor = "#0079C0"; e.target.style.boxShadow = "0 0 0 3px rgba(0,121,192,0.1)"; }}
                onBlur={e => { e.target.style.borderColor = "#d1d5db"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <div>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                style={inputStyle}
                type="password"
                value={pw.confirm}
                onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))}
                placeholder="Confirm new password"
                onFocus={e => { e.target.style.borderColor = "#0079C0"; e.target.style.boxShadow = "0 0 0 3px rgba(0,121,192,0.1)"; }}
                onBlur={e => { e.target.style.borderColor = "#d1d5db"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <button
              type="submit"
              disabled={pwSaving}
              style={{
                alignSelf: "flex-start",
                background: "#0079C0",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "0.6rem 1.5rem",
                fontWeight: "600",
                fontSize: "0.95rem",
                cursor: pwSaving ? "not-allowed" : "pointer",
                opacity: pwSaving ? 0.7 : 1,
                transition: "background 0.15s",
              }}
            >
              {pwSaving ? "Changing…" : "Change Password"}
            </button>
            {msgBox(pwMsg)}
          </form>
        </div>

      </div>
    </motion.div>
  );
}
